import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Country, State } from 'country-state-city';
import { getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import { useUser } from '../../../../contexts/UserContext';
import { PT, dialogPaperSx, fieldSx, kickerSx, primaryBtnSx } from '../../../../components/portalUi';
import { useNetwork } from '../../../../utils/network';
import { useShowSnackbar } from '../../../../utils/snackbar';
import {
  countryHasStates,
  findCountryIso2,
  missingProfileDetails
} from '../../../../utils/profileCompleteness';

/* THE DETAILS THE QR SIGN-UP NO LONGER ASKS FOR (2026-10-07).
 *
 * The poster funnel creates an account from a name, a username and an email
 * alone, so a member can reach the portal with no country, state or mobile on
 * file. This dialog collects them the first time they are here — and holds them
 * here: no close button, no backdrop click, no Escape. The only way out is a
 * saved set of details, after which `isProfileIncomplete` (the one rule both
 * sides of this dialog read) says the dialog is no longer owed and the shell
 * stops rendering it.
 *
 * Shown by `DynamicMemberPortal` for ANY account the details are missing on, not
 * only QR ones — an older member with a blank state in a country that has them
 * sees it once too. It never clears a value: the server refuses a blank country
 * or an unusable mobile rather than storing it.
 *
 * The phone goes up as the local number plus its calling code, which is how
 * `SettingsUserDetails` already sends `PUT /user/phone`; the server normalises
 * and checks it the same way for both (`userController.completeProfileDetails`).
 */
const callingCodeFor = (iso2: string) => {
  try {
    return iso2 ? String(getCountryCallingCode(iso2 as CountryCode)) : '';
  } catch {
    return '';
  }
};

/* The digits a member would type, from a stored number — `+61412000111` with
   calling code 61 → `412000111` — so an account that has a phone but lacks a
   state does not have to retype it. */
const localPhone = (phone: string | undefined, callingCode: string) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  return callingCode && digits.startsWith(callingCode)
    ? digits.slice(callingCode.length)
    : digits;
};

/* THE PLATFORM'S PHONE RULE, as the sign-up page and the settings page apply it:
   digits only, then the number must be a real one for the chosen country
   (libphonenumber `isPossible` + `isValid`). The server only bounds the length
   (8–15 digits with the country code, `completeProfileDetails`), so this is where
   the shape is held — and the admin's explicit rule for Australia is stated as
   such: +61 followed by nine digits, which is every Australian mobile (and
   landline), never a 13/1300/1800 number. A leading 0 is how Australians write a
   mobile (0412 …); it is dropped before the check, as the server drops it before
   storing +61 412 …. */
const AU_NATIONAL_DIGITS = 9;
/* THE PLATFORM'S NAME RULE, as the server applies it (utils/validation.js
   validateName): letters in any script, spaces, hyphens, apostrophes and periods,
   at most 50 characters. Held here too so a slip is explained before the save. */
const validateName = (value: string, label: string) => {
  const trimmed = value.trim();
  if (!trimmed) return `${label} is required.`;
  if (trimmed.length > 50) return `${label} cannot exceed 50 characters.`;
  if (!/^[\p{L}\p{M} '’.-]+$/u.test(trimmed)) {
    return `${label} can only contain letters, spaces, hyphens, apostrophes and periods.`;
  }
  return '';
};
const TERMS_URL =
  'https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/documents/terms_and_conditions_05_08_2026.docx';

const validatePhone = (local: string, iso2: string, callingCode: string) => {
  const digits = local.replace(/\s/g, '').trim();
  if (!digits) return 'Mobile number is required.';
  if (!/^\d+$/.test(digits)) return 'Mobile number can only contain numbers.';
  const national = digits.replace(/^0+/, '');
  if (iso2 === 'AU' && national.length !== AU_NATIONAL_DIGITS) {
    return 'An Australian mobile is 9 digits after +61, e.g. 412 345 678.';
  }
  try {
    const parsed = parsePhoneNumberFromString(
      `+${callingCode}${national}`,
      iso2 as CountryCode
    );
    if (!parsed || !parsed.isPossible()) {
      return 'Mobile number looks too short or too long.';
    }
    if (!parsed.isValid()) {
      return 'Enter a valid mobile number for your country.';
    }
  } catch {
    return 'Enter a valid mobile number.';
  }
  return '';
};

export const ProfileDetailsModal = ({ open }: { open: boolean }) => {
  const { user, setUser } = useUser();
  const { put } = useNetwork();
  const { showSnackbar } = useShowSnackbar();

  const countries = useMemo(() => Country.getAllCountries(), []);
  /* What is on file is the starting point. Nothing on file (the QR sign-up) starts
     on Australia, where nearly every member is; a country we cannot match to the
     list (an older free-text value) starts on NOTHING, so the member has to choose
     and a save never quietly turns "Viet Nam" into Australia. */
  const storedCountry = String(user?.country || '').trim();
  const [countryIso2, setCountryIso2] = useState<string>(
    () => findCountryIso2(storedCountry) ?? (storedCountry ? '' : 'AU')
  );
  const states = useMemo(
    () => State.getStatesOfCountry(countryIso2) || [],
    [countryIso2]
  );
  const [stateIso2, setStateIso2] = useState<string>(() => {
    const wanted = String(user?.state || '')
      .trim()
      .toLowerCase();
    if (!wanted) return '';
    const iso2 = findCountryIso2(user?.country) ?? 'AU';
    const match = (State.getStatesOfCountry(iso2) || []).find(
      (s) => s.isoCode.toLowerCase() === wanted || s.name.toLowerCase() === wanted
    );
    return match ? match.isoCode : '';
  });
  const callingCode = callingCodeFor(countryIso2);
  const [phone, setPhone] = useState<string>(() =>
    localPhone(
      user?.phone,
      callingCodeFor(findCountryIso2(user?.country) ?? (storedCountry ? '' : 'AU'))
    )
  );
  /* EVERY profile field lives here; only the ones the account lacks are shown.
     A member a partner (Empire) sent us arrives with no username and no names —
     the partner's display name is not stored — as well as no mobile; a QR member
     with no country, state or mobile; an older member with one blank. The one
     rule, `missingProfileDetails`, decides both what to show and when the dialog
     is owed. */
  const missing = useMemo(() => new Set(missingProfileDetails(user)), [user]);
  const owed = (
    f: 'username' | 'firstName' | 'lastName' | 'country' | 'state' | 'phone' | 'giveawayEligibility'
  ) => missing.has(f);
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const lastNameOwed = owed('lastName');
  /* The giveaway question, asked here when the account's answer is unknown —
     the same two things the old eligibility dialog asked: that the T&Cs were
     read, and whether the member is eligible. */
  const [readTerms, setReadTerms] = useState(false);
  const [eligible, setEligible] = useState<boolean | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');

  const errors = {
    username: owed('username')
      ? !username.trim()
        ? 'Username is required.'
        : !/^[A-Za-z0-9_]{3,20}$/.test(username.trim())
          ? 'Use 3-20 letters, numbers, or underscores.'
          : ''
      : '',
    firstName: owed('firstName') ? validateName(firstName, 'First name') : '',
    lastName: lastNameOwed ? validateName(lastName, 'Last name') : '',
    giveawayEligibility: owed('giveawayEligibility')
      ? !readTerms
        ? 'Please confirm you have read the T&Cs.'
        : eligible === null
          ? 'Please tell us whether you are eligible for giveaways.'
          : ''
      : '',
    country: countryIso2 ? '' : 'Country is required.',
    state:
      countryHasStates(countryIso2) && !stateIso2
        ? 'State/Province is required.'
        : '',
    phone: validatePhone(phone, countryIso2, callingCode)
  };
  const fieldErr = (key: keyof typeof errors) =>
    attempted && errors[key] ? errors[key] : '';

  const save = async () => {
    setAttempted(true);
    setServerError('');
    if (Object.values(errors).some(Boolean)) return;

    const country = countries.find((c) => c.isoCode === countryIso2);
    const state = states.find((s) => s.isoCode === stateIso2);
    setSaving(true);
    try {
      const result = await put(
        '/user/profile-details',
        {
          country: country?.name ?? countryIso2,
          state: state?.name ?? '',
          phone: phone.trim(),
          countryCode: callingCode,
          ...(owed('username') ? { username: username.trim() } : {}),
          ...(owed('firstName') ? { firstName: firstName.trim() } : {}),
          ...(lastNameOwed ? { lastName: lastName.trim() } : {}),
          ...(owed('giveawayEligibility') ? { isGiveawayEligible: eligible } : {})
        },
        true
      );
      /* Writing these onto the user is what closes the dialog: the shell asks
         `isProfileIncomplete(user)` on every render. */
      setUser({
        ...user,
        country: result?.country ?? country?.name ?? countryIso2,
        state: result?.state ?? state?.name ?? '',
        phone: result?.phone ?? `+${callingCode}${phone.trim()}`,
        phoneVerified: result?.phoneVerified ?? false,
        ...(owed('username') ? { username: result?.username ?? username.trim() } : {}),
        ...(owed('firstName') ? { firstName: result?.firstName ?? firstName.trim() } : {}),
        ...(lastNameOwed ? { lastName: result?.lastName ?? lastName.trim() } : {}),
        ...(owed('giveawayEligibility') ? { isGiveawayEligible: result?.isGiveawayEligible ?? eligible } : {})
      });
      showSnackbar(result?.message || 'Your details are saved.', 'success');
    } catch (error: any) {
      /* useNetwork throws a plain Error with .message/.status/.data — never an
         axios `.response` (see the username dialog in DynamicMemberPortal). */
      const message =
        error?.message ||
        error?.data?.message ||
        'Could not save your details. Please try again.';
      setServerError(message);
      showSnackbar(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const intro = (() => {
    const names: Record<string, string> = {
      username: 'a username',
      firstName: 'your first name',
      lastName: 'your last name',
      country: 'your country',
      state: 'your state',
      phone: 'your mobile number',
      giveawayEligibility: 'whether you are eligible for giveaways'
    };
    const list = (['username', 'firstName', 'lastName', 'country', 'state', 'phone', 'giveawayEligibility'] as const)
      .filter((f) => missing.has(f))
      .map((f) => names[f]);
    const words =
      list.length <= 1 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
    return `We need ${words || 'a few details'} before you can continue. It only takes a moment.`;
  })();
  const textField = (props: React.ComponentProps<typeof TextField>) => (
    <TextField fullWidth size="small" sx={{ ...fieldSx, mb: 2 }} {...props} />
  );

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown
      /* Above every other modal. The dashboard opens its own two-factor nudge
         on first visit, which would otherwise paint OVER this (later in the DOM
         wins at equal z-index) and let the member close that one and then face
         this; the details come first, and the nudge is there when they are done. */
      sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '18px',
              display: 'grid',
              placeItems: 'center',
              color: PT.teal,
              background: PT.tealSoft,
              border: `1px solid ${PT.tealBorder}`
            }}
          >
            <PersonAddAltRoundedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography sx={{ ...kickerSx, mb: 1 }}>Your profile</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 22, color: PT.text, lineHeight: 1.2 }}>
            Complete your details
          </Typography>
          <Typography sx={{ mt: 1, color: PT.textMuted, fontSize: 14.5 }}>{intro}</Typography>
        </Box>

        {owed('username') &&
          textField({
            label: 'Username',
            value: username,
            autoComplete: 'username',
            onChange: (event) => {
              setUsername(event.target.value);
              setServerError('');
            },
            error: Boolean(fieldErr('username')),
            helperText: fieldErr('username') || '3-20 characters: A-Z, 0-9, _ — shown around the portal, marketplace and games'
          })}
        {owed('firstName') &&
          textField({
            label: 'First name',
            value: firstName,
            autoComplete: 'given-name',
            onChange: (event) => {
              setFirstName(event.target.value);
              setServerError('');
            },
            error: Boolean(fieldErr('firstName')),
            helperText: fieldErr('firstName') || ' '
          })}
        {lastNameOwed &&
          textField({
            label: 'Last name',
            value: lastName,
            autoComplete: 'family-name',
            onChange: (event) => {
              setLastName(event.target.value);
              setServerError('');
            },
            error: Boolean(fieldErr('lastName')),
            helperText: fieldErr('lastName') || ' '
          })}
        {(owed('country') || owed('state')) && (
          <>
            <FormControl fullWidth size="small" sx={{ ...fieldSx, mb: 2 }} error={Boolean(fieldErr('country'))}>
              <InputLabel id="profile-country-label">Country</InputLabel>
              <Select
                labelId="profile-country-label"
                label="Country"
                value={countryIso2}
                displayEmpty
                onChange={(event) => {
                  const iso2 = String(event.target.value);
                  setCountryIso2(iso2);
                  setStateIso2('');
                }}
              >
                {countries.map((c) => (
                  <MenuItem key={c.isoCode} value={c.isoCode}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErr('country') && <FormHelperText>{fieldErr('country')}</FormHelperText>}
            </FormControl>
            {/* Only where the country has states to pick from; nothing to answer otherwise. */}
            {states.length > 0 && (
              <FormControl fullWidth size="small" sx={{ ...fieldSx, mb: 2 }} error={Boolean(fieldErr('state'))}>
                <InputLabel id="profile-state-label">State/Province</InputLabel>
                <Select
                  labelId="profile-state-label"
                  label="State/Province"
                  value={stateIso2}
                  onChange={(event) => setStateIso2(String(event.target.value))}
                >
                  {states.map((s) => (
                    <MenuItem key={s.isoCode} value={s.isoCode}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErr('state') && <FormHelperText>{fieldErr('state')}</FormHelperText>}
              </FormControl>
            )}
          </>
        )}
        {owed('phone') &&
          textField({
            label: 'Mobile number',
            value: phone,
            inputMode: 'tel',
            autoComplete: 'tel-national',
            onChange: (event) => {
              setPhone(event.target.value);
              setServerError('');
            },
            onKeyDown: (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                save();
              }
            },
            error: Boolean(fieldErr('phone') || serverError),
            helperText: fieldErr('phone') || serverError || ' ',
            InputProps: {
              startAdornment: callingCode ? (
                <InputAdornment position="start">+{callingCode}</InputAdornment>
              ) : undefined
            }
          })}
        {owed('giveawayEligibility') && (
          <Box
            sx={{
              mb: 1,
              p: 2,
              borderRadius: '12px',
              border: `1px solid ${fieldErr('giveawayEligibility') ? '#ff6b6b' : PT.border}`,
              background: 'rgba(255,255,255,0.03)'
            }}
          >
            <Typography sx={{ ...kickerSx, mb: 1 }}>Giveaways</Typography>
            <Typography sx={{ color: PT.textMuted, fontSize: 13.5, mb: 1 }}>
              Our T&amp;Cs are here:{' '}
              <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer" sx={{ color: PT.teal }}>
                INCRYPT Terms &amp; Conditions
              </Link>
            </Typography>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  checked={readTerms}
                  onChange={(e) => setReadTerms(e.target.checked)}
                  sx={{ color: PT.textMuted, '&.Mui-checked': { color: PT.teal }, pt: 0.25 }}
                />
              }
              label={
                <Typography sx={{ fontSize: 14, color: PT.text }}>
                  I have read and agree to the{' '}
                  <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer" sx={{ color: PT.teal }}>
                    terms and conditions
                  </Link>
                </Typography>
              }
            />
            <FormLabel
              id="profile-giveaway-eligibility"
              sx={{ display: 'block', mt: 1.5, mb: 0.5, fontSize: 13.5, color: `${PT.textMuted} !important` }}
            >
              Are you eligible for giveaways?
            </FormLabel>
            <RadioGroup
              aria-labelledby="profile-giveaway-eligibility"
              value={eligible === null ? '' : eligible ? 'yes' : 'no'}
              onChange={(e) => setEligible(e.target.value === 'yes')}
            >
              {[
                ['yes', 'Yes, I am eligible for giveaways'],
                ['no', 'No, I am not eligible for giveaways']
              ].map(([value, label]) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={<Radio sx={{ color: PT.textMuted, '&.Mui-checked': { color: PT.teal } }} />}
                  label={<Typography sx={{ fontSize: 14, color: PT.text }}>{label}</Typography>}
                />
              ))}
            </RadioGroup>
            {fieldErr('giveawayEligibility') && (
              <FormHelperText error sx={{ ml: 0 }}>
                {fieldErr('giveawayEligibility')}
              </FormHelperText>
            )}
          </Box>
        )}

        <Button fullWidth disabled={saving} onClick={save} sx={{ ...primaryBtnSx, mt: 2, py: 1.1 }}>
          {saving ? 'Saving…' : 'Save details'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ProfileDetailsModal;
